import React, { useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';

const MaterialSection = styled.div`
  margin-bottom: 1.5rem;
`;

const MaterialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  width: 100%;
`;

const AddButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  min-width: 100px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: #c82333;
  }
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  margin-left: 0.5rem;
`;

const HelpText = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
  font-style: italic;
  margin-left: 1rem;
`;

const MaterialItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  width: 100%;
  
  /* Ensure select takes most of the space */
  > div:first-child {
    flex: 1;
  }
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  width: 120px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    width: '100%',
    borderColor: '#ddd',
    '&:hover': {
      borderColor: '#4CAF50'
    }
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

// Helper to normalize decimal input (accept both comma and dot)
const normalizeDecimal = (value) => {
  if (typeof value === 'string') {
    return value.replace(',', '.');
  }
  return value;
};

const MaterialSelector = ({ products, register, control, setValue, watch, measurementType }) => {
  const [molduras, setMolduras] = useState([]);
  const [passepartouts, setPassepartouts] = useState([]);
  const [cristales, setCristales] = useState([]);
  const [traseras, setTraseras] = useState([]);
  const [accesorios, setAccesorios] = useState([]);

  // Watch all arrays
  const watchedMolduras = watch('molduras') || [];
  const watchedPassepartouts = watch('passepartouts') || [];
  const watchedCristales = watch('cristales') || [];
  const watchedTraseras = watch('traseras') || [];
  const watchedAccesorios = watch('accesorios') || [];

  const addMoldura = () => {
    const newId = `moldura_${Date.now()}`;
    setMolduras([...molduras, newId]);
  };

  const removeMoldura = (index) => {
    const newMolduras = molduras.filter((_, i) => i !== index);
    setMolduras(newMolduras);
    const currentValues = watchedMolduras.filter((_, i) => i !== index);
    setValue('molduras', currentValues);
  };

  const addPassepartout = () => {
    const newId = `passepartout_${Date.now()}`;
    setPassepartouts([...passepartouts, newId]);
  };

  const removePassepartout = (index) => {
    const newPassepartouts = passepartouts.filter((_, i) => i !== index);
    setPassepartouts(newPassepartouts);
    const currentValues = watchedPassepartouts.filter((_, i) => i !== index);
    setValue('passepartouts', currentValues);
  };

  const addCristal = () => {
    const newId = `cristal_${Date.now()}`;
    setCristales([...cristales, newId]);
  };

  const removeCristal = (index) => {
    const newCristales = cristales.filter((_, i) => i !== index);
    setCristales(newCristales);
    const currentValues = watchedCristales.filter((_, i) => i !== index);
    setValue('cristales', currentValues);
  };

  const addTrasera = () => {
    const newId = `trasera_${Date.now()}`;
    setTraseras([...traseras, newId]);
  };

  const removeTrasera = (index) => {
    const newTraseras = traseras.filter((_, i) => i !== index);
    setTraseras(newTraseras);
    const currentValues = watchedTraseras.filter((_, i) => i !== index);
    setValue('traseras', currentValues);
  };

  const addAccesorio = () => {
    const newId = `accesorio_${Date.now()}`;
    setAccesorios([...accesorios, newId]);
  };

  const removeAccesorio = (index) => {
    const newAccesorios = accesorios.filter((_, i) => i !== index);
    setAccesorios(newAccesorios);
    const currentValues = watchedAccesorios.filter((_, i) => i !== index);
    setValue('accesorios', currentValues);
  };

  const formatProductOptions = (productList) => {
    return productList.map(product => ({
      value: product.id,
      label: `${product.name} - €${product.basePrice}/${product.unit}`,
      product
    }));
  };

  return (
    <>
      {/* Passepartout Section */}
      <MaterialSection>
        <MaterialRow>
          <AddButton 
            onClick={addPassepartout} 
            disabled={passepartouts.length >= 3}
          >
            AÑADIR
          </AddButton>
          <Label>Passepartout</Label>
          <HelpText>* puedes añadir hasta 3 tipos de passepartout.</HelpText>
        </MaterialRow>
        {passepartouts.map((id, index) => (
          <MaterialItem key={id}>
            <Select
              styles={customSelectStyles}
              options={formatProductOptions(products.passepartout || [])}
              placeholder="Selecciona una opción"
              onChange={(option) => {
                const currentValues = [...(watchedPassepartouts || [])];
                currentValues[index] = {
                  productId: option?.value || '',
                  width: currentValues[index]?.width || ''
                };
                setValue('passepartouts', currentValues);
              }}
            />
            <Input
              type="text"
              inputMode="decimal"
              placeholder="Ancho (cm)"
              onChange={(e) => {
                const currentValues = [...(watchedPassepartouts || [])];
                currentValues[index] = {
                  productId: currentValues[index]?.productId || '',
                  width: parseFloat(normalizeDecimal(e.target.value)) || 0
                };
                setValue('passepartouts', currentValues);
              }}
            />
            <RemoveButton onClick={() => removePassepartout(index)}>
              ELIMINAR
            </RemoveButton>
          </MaterialItem>
        ))}
      </MaterialSection>

      {/* Cristales Section */}
      <MaterialSection>
        <MaterialRow>
          <AddButton 
            onClick={addCristal} 
            disabled={cristales.length >= 2}
          >
            AÑADIR
          </AddButton>
          <Label>Cristales</Label>
          <HelpText>* puedes añadir hasta 2 tipos de cristales.</HelpText>
        </MaterialRow>
        {cristales.map((id, index) => (
          <MaterialItem key={id}>
            <Select
              styles={customSelectStyles}
              options={formatProductOptions(products.cristales || [])}
              placeholder="Selecciona una opción"
              onChange={(option) => {
                const currentValues = [...(watchedCristales || [])];
                currentValues[index] = option?.value || '';
                setValue('cristales', currentValues);
              }}
            />
            <RemoveButton onClick={() => removeCristal(index)}>
              ELIMINAR
            </RemoveButton>
          </MaterialItem>
        ))}
      </MaterialSection>

      {/* Traseras Section */}
      <MaterialSection>
        <MaterialRow>
          <AddButton 
            onClick={addTrasera} 
            disabled={traseras.length >= 3}
          >
            AÑADIR
          </AddButton>
          <Label>Traseras</Label>
          <HelpText>* puedes añadir hasta 3 tipos traseras.</HelpText>
        </MaterialRow>
        {traseras.map((id, index) => (
          <MaterialItem key={id}>
            <Select
              styles={customSelectStyles}
              options={formatProductOptions(products.traseras || [])}
              placeholder="Selecciona una opción"
              onChange={(option) => {
                const currentValues = [...(watchedTraseras || [])];
                currentValues[index] = option?.value || '';
                setValue('traseras', currentValues);
              }}
            />
            <RemoveButton onClick={() => removeTrasera(index)}>
              ELIMINAR
            </RemoveButton>
          </MaterialItem>
        ))}
      </MaterialSection>

      {/* Moldura Section */}
      <MaterialSection>
        <MaterialRow>
          <AddButton 
            onClick={addMoldura} 
            disabled={molduras.length >= 3}
          >
            AÑADIR
          </AddButton>
          <Label>Moldura</Label>
          <HelpText>* puedes añadir hasta 3 tipos molduras.</HelpText>
        </MaterialRow>
        {molduras.map((id, index) => (
          <MaterialItem key={id}>
            <Select
              styles={customSelectStyles}
              options={formatProductOptions(products.molduras || [])}
              placeholder="Selecciona una opción"
              onChange={(option) => {
                const currentValues = [...(watchedMolduras || [])];
                currentValues[index] = option?.value || '';
                setValue('molduras', currentValues);
              }}
            />
            <RemoveButton onClick={() => removeMoldura(index)}>
              ELIMINAR
            </RemoveButton>
          </MaterialItem>
        ))}
      </MaterialSection>

      {/* Accesorios Section */}
      <MaterialSection>
        <MaterialRow>
          <AddButton 
            onClick={addAccesorio} 
            disabled={accesorios.length >= 4}
          >
            AÑADIR
          </AddButton>
          <Label>Accesorios</Label>
          <HelpText>* puedes añadir hasta 4 tipos accesorios.</HelpText>
        </MaterialRow>
        {accesorios.map((id, index) => (
          <MaterialItem key={id}>
            <Select
              styles={customSelectStyles}
              options={formatProductOptions(products.accesorios || [])}
              placeholder="Selecciona una opción"
              onChange={(option) => {
                const currentValues = [...(watchedAccesorios || [])];
                currentValues[index] = option?.value || '';
                setValue('accesorios', currentValues);
              }}
            />
            <RemoveButton onClick={() => removeAccesorio(index)}>
              ELIMINAR
            </RemoveButton>
          </MaterialItem>
        ))}
      </MaterialSection>
    </>
  );
};

export default MaterialSelector;